/**
 * Einen Fehler anzeigen — nach dem Fehlervertrag aus `04` §4.6.
 *
 * Der Satz, um den es geht, steht dort am Ende: „An Benutzeroberflächen werden
 * keine internen Stacktraces, Tokens, Bucket-Namen oder Originalantworten
 * weitergegeben." Dieses Bauteil ist die Stelle, an der das durchgesetzt wird —
 * und zwar durch das, was es **nicht** anzeigt.
 *
 * **Es zeigt weder `message` noch `details` noch `provider`.** Das klingt
 * streng und ist die einzige Auslegung, die trägt: Alle drei Felder füllt ein
 * Adapter aus dem, was ein fremder Dienst geantwortet hat. Ob dort ein
 * Bucketname, ein Pfad, ein Tokenfragment oder ein Stacktrace steht, weiß die
 * Oberfläche nicht — sie kann es nicht prüfen, also darf sie es nicht zeigen.
 * Was der Benutzer stattdessen bekommt, ist ein Text je Fehlercode, den wir
 * geschrieben haben.
 *
 * Die Felder gehen deshalb nicht verloren: Sie gehören ins Protokoll
 * (`packages/telemetry`, mit Redaction nach ADR-030) und in den Fehlerbericht.
 * Der Benutzer bekommt die **Correlation-ID**, damit die Meldung „bei mir geht
 * es nicht" und der Protokolleintrag zusammenfinden.
 */

import { FEHLERCODE, type ErrorEnvelope, type Fehlercode } from "@hwc/contracts";
import type { ReactNode } from "react";

/**
 * Ein Text je Code. Vollständig, und ein Test hält das fest — ein Fehlercode
 * ohne Text erschiene sonst als leerer Kasten, und zwar genau dann, wenn
 * ohnehin schon etwas schiefgegangen ist.
 *
 * Die Texte sagen, **was los ist und was zu tun ist**, nicht was technisch
 * passiert ist. „RESOURCE_FORBIDDEN" ist für den Benutzer keine Auskunft.
 */
const TEXT: Record<Fehlercode, { readonly was: string; readonly tun: string }> = {
  AUTH_REQUIRED: {
    was: "Die Anmeldung ist abgelaufen.",
    tun: "Bitte neu anmelden. Eingaben in offenen Formularen gehen dabei verloren.",
  },
  TOKEN_EXPIRED: {
    was: "Die Sitzung ist abgelaufen.",
    tun: "Bitte die Seite neu laden.",
  },
  RESOURCE_NOT_FOUND: {
    was: "Der Eintrag wurde nicht gefunden.",
    tun: "Möglicherweise wurde er verschoben oder gelöscht. Die Übersicht zeigt den aktuellen Stand.",
  },
  RESOURCE_FORBIDDEN: {
    was: "Für diesen Eintrag fehlt die Berechtigung.",
    tun: "Wer Zugriff braucht, wendet sich an die Betriebsleitung.",
  },
  CONFLICT: {
    was: "Der Eintrag wurde zwischenzeitlich geändert.",
    tun: "Bitte den aktuellen Stand ansehen und die Änderung erneut eintragen.",
  },
  RATE_LIMITED: {
    was: "Zu viele Anfragen in kurzer Zeit.",
    tun: "In einem Moment noch einmal versuchen.",
  },
  PROVIDER_UNAVAILABLE: {
    was: "Ein beteiligter Dienst antwortet gerade nicht.",
    tun: "Der Versuch kann wiederholt werden. Hält es an, hilft die Betriebsleitung weiter.",
  },
  VALIDATION_FAILED: {
    was: "Die Eingabe ist unvollständig oder nicht zulässig.",
    tun: "Bitte die markierten Felder prüfen.",
  },
  PAYLOAD_TOO_LARGE: {
    was: "Die Datei ist zu groß.",
    tun: "Eine kleinere Fassung hochladen oder die Datei aufteilen.",
  },
  UNSUPPORTED_CAPABILITY: {
    was: "Das ist an dieser Stelle nicht möglich.",
    tun: "Der beteiligte Dienst unterstützt diesen Vorgang nicht.",
  },
};

export interface FehlermeldungEigenschaften {
  readonly fehler: ErrorEnvelope;
  /** Gesetzt, wenn der Vorgang wiederholbar ist — zeigt einen Knopf. */
  readonly beiWiederholen?: () => void;
  /** Zusätzlicher Hinweis aus dem Zusammenhang der Ansicht. */
  readonly kinder?: ReactNode;
}

export function Fehlermeldung({
  fehler,
  beiWiederholen,
  kinder,
}: FehlermeldungEigenschaften) {
  const text = TEXT[fehler.code];
  const zeigeWiederholen = fehler.retryable && beiWiederholen;

  return (
    <div className="fehlermeldung" role="alert">
      {/* `alert` und nicht `status`: Ein Fehler unterbricht, was der Benutzer
          gerade vorhatte - anders als eine Speichermeldung. */}
      <p className="fehlermeldung__was">{text.was}</p>
      <p className="fehlermeldung__tun">{text.tun}</p>
      {kinder ? <p className="fehlermeldung__mehr">{kinder}</p> : null}

      {zeigeWiederholen ? (
        <p>
          <button type="button" className="knopf knopf--neben" onClick={beiWiederholen}>
            Erneut versuchen
          </button>
        </p>
      ) : null}

      {/* Die einzige technische Angabe, die der Benutzer sieht - und die
          einzige, die er braucht, wenn er den Fehler meldet. Sie enthält
          keine Daten, sondern verbindet seine Meldung mit dem
          Protokolleintrag. */}
      <p className="fehlermeldung__kennung">
        Kennung für Rückfragen: <code>{fehler.correlationId}</code>
      </p>
    </div>
  );
}

/** Für Prüfrezepte und das Handbuch: Ist für jeden Code ein Text hinterlegt? */
export function textLuecken(): readonly Fehlercode[] {
  return FEHLERCODE.filter((c) => !TEXT[c] || !TEXT[c].was || !TEXT[c].tun);
}
